import React, { useEffect, useState, useCallback } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faMapMarkerAlt, faTrash } from "@fortawesome/free-solid-svg-icons";
import BottomMenu from "../components/BottomMenu";


const API = "https://boxmealadminapinew.azurewebsites.net";
const DEV =
    process.env.REACT_APP_DEV_BYPASS === "1" ||
    localStorage.getItem("DEV_BYPASS") === "1";

export default function UnitsScreen() {
    const nav = useNavigate();
    const { user, token } = useAuth() || {};
    const userId = user?.id || "dev-user-1";
    const authToken = token || (DEV ? "dev-token" : undefined);

    const [loading, setLoading] = useState(true);
    const [fridges, setFridges] = useState([]);
    const [error, setError] = useState("");

    const [confirm, setConfirm] = useState({ open: false, target: null });
    const [deletingId, setDeletingId] = useState(null);


    const loadFridges = useCallback(async () => {
        setLoading(true);
        setError("");

        const authHeaders = authToken ? { Authorization: `Bearer ${authToken}` } : {};

        try {
            // Fetch only fridges the current user has added/access to
            const byUser = await fetch(`${API}/api/Fridge/by-user/${userId}`, {
                headers: { ...authHeaders },
            });
            if (!byUser.ok) throw new Error(`HTTP ${byUser.status}`);
            const data = await byUser.json();
            const list = (Array.isArray(data) ? data : [])
                // Exclude revoked accesses if provided by API
                .filter((f) => f.isRevoked !== true)
                .map((f) => ({
                    id: f.fridgeId ?? f.id,
                    name: f.fridgeName ?? f.name,
                }));
            setFridges(list);
        } catch (err) {
            console.warn("[Units] load by-user failed:", err);
            setError("Failed to load fridges.");
        } finally {
            setLoading(false);
        }
    }, [authToken, userId]);

    async function pick(fridgeId) {
        localStorage.setItem("selectedFridgeId", String(fridgeId));
        // Fetch fridge details to persist voucher flag
        try {
            const resp = await fetch(`${API}/api/Fridge/${fridgeId}`);
            if (resp.ok) {
                const data = await resp.json();
                const flag = !!data.isVoucherFridge;
                localStorage.setItem("isVoucherFridge", JSON.stringify(flag));
                console.log("UnitsScreen: pick() stored", { id: fridgeId, api: data.isVoucherFridge, saved: flag, ls: localStorage.getItem("isVoucherFridge") });
            } else {
                console.warn("UnitsScreen: pick() fetch fridge failed", resp.status);
            }
        } catch (e) {
            console.warn("UnitsScreen: pick() fetch error", e);
            // leave previous value if fetch fails
        }
        nav("/products");
    }

    useEffect(() => {
        loadFridges();
    }, [loadFridges]);

    function openDeleteConfirm(fridge) {
        setConfirm({ open: true, target: fridge });
    }

    function closeDeleteConfirm() {
        setConfirm({ open: false, target: null });
    }

    async function removeFridge() {
        const target = confirm.target;
        if (!target) return;

        setDeletingId(target.id);
        try {
            const authHeaders = authToken ? { Authorization: `Bearer ${authToken}` } : {};
            const url = `${API}/api/fridge/remove-user-fridge?userId=${encodeURIComponent(userId)}&fridgeId=${encodeURIComponent(target.id)}`;
            const resp = await fetch(url, { method: "DELETE", headers: { ...authHeaders } });

            if (!resp.ok) {
                const txt = await resp.text().catch(() => "");
                throw new Error(txt || `HTTP ${resp.status}`);
            }

            setFridges(prev => prev.filter(f => String(f.id) !== String(target.id)));

            if (localStorage.getItem("selectedFridgeId") === String(target.id)) {
                localStorage.removeItem("selectedFridgeId");
            }

            closeDeleteConfirm();
        } catch (e) {
            console.error("Remove fridge failed:", e);
            alert("Kunde inte koppla bort enheten.");
        } finally {
            setDeletingId(null);
        }
    }


    return (
        <Container>
            <Header>
                <BackButton onClick={() => nav(-1)}>
                    <FontAwesomeIcon icon={faArrowLeft} />
                </BackButton>
                <HeaderTitle>Mina enheter</HeaderTitle>
            </Header>

            <ContentWrapper>
                {loading ? (
                    <LoadingContainer>
                        <LoadingText>Laddar enheter...</LoadingText>
                        <SkeletonContainer>
                            <SkeletonCard />
                            <SkeletonCard />
                            <SkeletonCard />
                        </SkeletonContainer>
                    </LoadingContainer>
                ) : error ? (
                    <ErrorContainer>
                        <ErrorText>{error}</ErrorText>
                        <RetryButton onClick={loadFridges}>Försök igen</RetryButton>
                    </ErrorContainer>
                ) : fridges.length === 0 ? (
                    <EmptyContainer>
                        <EmptyIcon>
                            <FontAwesomeIcon icon={faMapMarkerAlt} size="3x" />
                        </EmptyIcon>
                        <EmptyTitle>Inga enheter hittades</EmptyTitle>
                        <EmptyText>Du har inga enheter kopplade till ditt konto än.</EmptyText>
                    </EmptyContainer>
                ) : (
                    <UnitsGrid>
                        {fridges.map((f) => (
                            <UnitCard key={f.id} onClick={() => pick(f.id)}>
                                <UnitIcon>
                                    <FontAwesomeIcon icon={faMapMarkerAlt} />
                                </UnitIcon>

                                <UnitInfo>
                                    <UnitName>{f.name}</UnitName>
                                    <UnitId>ID: {f.id}</UnitId>
                                </UnitInfo>

                                <RightCol>
                                    <SelectButton>Välj</SelectButton>
                                    <DeleteBtn
                                        title="Koppla bort enhet"
                                        aria-label="Koppla bort enhet"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            openDeleteConfirm(f);
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                    </DeleteBtn>
                                </RightCol>

                            </UnitCard>
                        ))}
                    </UnitsGrid>

                )}
            </ContentWrapper>

            {confirm.open && (
                <ConfirmOverlay onClick={closeDeleteConfirm}>
                    <ConfirmCard onClick={(e) => e.stopPropagation()}>
                        <ConfirmTitle>Är du säker på att koppla bort enheten?</ConfirmTitle>
                        <ConfirmText>
                            Du kommer inte längre ha åtkomst till <strong>{confirm.target?.name}</strong>.
                        </ConfirmText>

                        <ConfirmActions>
                            <ConfirmCancel onClick={closeDeleteConfirm}>Avbryt</ConfirmCancel>
                            <ConfirmDelete
                                onClick={removeFridge}
                                disabled={deletingId === confirm.target?.id}
                            >
                                {deletingId === confirm.target?.id ? "Kopplar bort..." : "Koppla bort"}
                            </ConfirmDelete>
                        </ConfirmActions>
                    </ConfirmCard>
                </ConfirmOverlay>
            )}


            <BottomMenuSpacer />
            <BottomMenu />
        </Container>
    );
}


const Container = styled.div`
  min-height: 100vh;
  background: #f6f7fa;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const BackButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.8);
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(10px);
  color: #374151;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.8);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const HeaderTitle = styled.h1`
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const ContentWrapper = styled.div`
  flex: 1;
  padding: 20px;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
`;

const LoadingText = styled.div`
  font-size: 16px;
  color: #6b7280;
  margin-bottom: 20px;
`;

const SkeletonContainer = styled.div`
  display: grid;
  gap: 16px;
  width: 100%;
  max-width: 400px;
`;

const SkeletonCard = styled.div`
  height: 80px;
  background: linear-gradient(90deg, #f3f4f6 0%, #eceff3 45%, #f3f4f6 90%);
  background-size: 300% 100%;
  border-radius: 16px;
  animation: shimmer 1.5s infinite ease;
  
  @keyframes shimmer {
    0% { background-position: 0 0; }
    100% { background-position: -300% 0; }
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
`;

const ErrorText = styled.div`
  font-size: 16px;
  color: #ef4444;
  text-align: center;
  margin-bottom: 20px;
`;

const RetryButton = styled.button`
  background: rgba(37, 37, 37, 0.9);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(37, 37, 37, 1);
    transform: translateY(-1px);
  }
`;

const EmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
`;

const EmptyIcon = styled.div`
  color: #d1d5db;
  margin-bottom: 20px;
`;

const EmptyTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 8px 0;
`;

const EmptyText = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 30px 0;
  line-height: 1.5;
`;

const UnitsGrid = styled.div`
  display: grid;
  gap: 16px;
  max-width: 600px;
  margin: 0 auto;
`;

const UnitCard = styled.div`
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.9);
  border-radius: 16px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

  &:hover {
    background: rgba(255, 255, 255, 0.9);
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  }
`;

const UnitIcon = styled.div`
  width: 48px;
  height: 48px;
  background: rgba(37, 37, 37, 0.1);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #374151;
  font-size: 20px;
`;

const UnitInfo = styled.div`
  flex: 1;
`;

const UnitName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 4px;
`;

const UnitId = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const SelectButton = styled.div`
  background: rgba(37, 37, 37, 0.9);
  color: white;
  padding: 8px 16px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.3s ease;

  ${UnitCard}:hover & {
    background: rgba(37, 37, 37, 1);
    transform: scale(1.05);
  }
`;

const BottomMenuSpacer = styled.div`
  height: 80px;
`;


const ConfirmOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ConfirmCard = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 20px;
  width: 92%;
  max-width: 420px;
  box-shadow: 0 20px 40px rgba(0,0,0,.2);
`;

const ConfirmTitle = styled.h3`
  margin: 0 0 8px 0;
  font-size: 18px;
  color: #1f2937;
  font-weight: 700;
`;

const ConfirmText = styled.p`
  margin: 0 0 16px 0;
  color: #374151;
  font-size: 14px;
`;

const ConfirmActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

const ConfirmCancel = styled.button`
  background: #e5e7eb;
  color: #374151;
  border: none;
  border-radius: 10px;
  padding: 10px 14px;
  font-weight: 600;
  cursor: pointer;
`;

const ConfirmDelete = styled.button`
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 10px;
  padding: 10px 14px;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    opacity: .7;
    cursor: not-allowed;
  }
`;

const RightCol = styled.div`
  margin-left: auto;            
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  min-width: 84px;              
`;

const DeleteBtn = styled.button`
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 10px;
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform .12s ease, box-shadow .2s ease, background .2s ease;

  &:hover {
    background: rgba(239, 68, 68, 0.2);
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(239, 68, 68, 0.25);
  }

  &:active {
    transform: translateY(0);
    box-shadow: none;
  }
`;
